import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import countries from '@/payloads/shipping-countries.json';
import { guid } from '@/mixins/guid';
import tooltip from '@/mixins/components/tooltip';
import { dynamicSetup, SHIPPING } from '@/mixins/dynamic-setup';
import { siteBuilder } from '@/mixins/site-builder';
import dropsLoader from '@/mixins/components/drops-loader';

const AAA_GLOBAL = {
  name: 'Global',
  iso: 'GLOBAL',
};

export default Vue.component('data-product-store-shipping', {
  template: require('./template.html'),
  name: 'data-product-store-shipping',
  mixins: [guid, dynamicSetup, siteBuilder],
  components: {
    tooltip,
    dropsLoader,
  },
  computed: {
    ...mapGetters([
      'currentSite',
      'activeSiteType',
    ]),
    storeData() {
      return this.currentSite.dynamic.products;
    },
    countries() {
      return countries;
    },
    shipToCountries() {
      const search = new RegExp(this.searchToCountries, 'gi');
      return _.pickBy({ AAA_GLOBAL, ...this.countries }, item => item.name.match(search));
    },
    selectedCountries() {
      return _.map(this.to, item => item.name).join(', ');
    },
    rulesTooltip() {
      return 'Make sure the "between" value is less than or equal to the "and" value. No values can be less than 0.';
    },
    priorityTooltip() {
      return `If you have multiple rates and a customer's order meets more than one, you can set the priority of which
      rate you'd rather use. The lower the number, the higher the priority. If priorities are all the same, the first
      matching rate will be used. If no rates match, the order defaults to free shipping.`;
    },
    ruleTypes() {
      return [
        'FLAT_RATE',
        'WEIGHT',
        'TOTAL',
        'QUANTITY',
      ];
    },
  },
  data() {
    return {
      loading: false,
      rateLoading: false,
      rates: [],
      rateItems: {},
      rateError: false,
      showToCountries: false,
      searchToCountries: '',
      to: {},
      initialEdits: {
        to: {},
        rates: [],
        rateItems: {},
      },
    };
  },
  props: [
    'item-modal',
    'content',
    'active-item',
    'item-list',
    'all-categories',
  ],
  created() {
    if (this.noDynamicData(this.activeSiteType)) {
      this.dynamicSetup(this.activeSiteType);
    }
    this.shippingCheck();
    this.setInitialValues();
  },
  methods: {
    setInitialValues() {
      const { shipping } = this.storeData;
      const to = shipping && shipping.to;
      const toShipping = to ? _.cloneDeep(to) : { AAA_GLOBAL };
      this.$set(this, 'to', toShipping);
      this.$set(this.initialEdits, 'to', _.cloneDeep(this.to));
      if (shipping.rates) {
        shipping.rates.forEach((rate) => {
          this.$set(this.rateItems, rate.id, {
            edit: false,
            delete: false,
            error: false,
          });
        });
        this.$set(this, 'rates', _.cloneDeep(shipping.rates));
        this.$set(this.initialEdits, 'rates', _.cloneDeep(shipping.rates));
        this.$set(this.initialEdits, 'rateItems', _.cloneDeep(this.rateItems));
      }
    },
    shippingCheck() {
      const { dynamic } = this.currentSite;
      if (!this.storeData.shipping) {
        this.$store.dispatch('setGlobalValue', {
          item: dynamic.products,
          key: 'shipping',
          value: _.cloneDeep(SHIPPING),
        });
      }
      if (!this.storeData.shipping.rates) {
        this.$store.dispatch('setGlobalValue', {
          item: dynamic.products.shipping,
          key: 'rates',
          value: [],
        });
      }
    },
    getRuleLabel(type) {
      const types = {
        FLAT_RATE: 'Flat Rate',
        WEIGHT: 'By Order Weight',
        TOTAL: 'By Order Total',
        QUANTITY: 'By Item Quantity',
      };
      return types[type];
    },
    getRuleUnit(type) {
      const types = {
        FLAT_RATE: 'Always',
        WEIGHT: 'Ounces',
        TOTAL: 'Dollars',
        QUANTITY: 'Items',
      };
      return types[type];
    },
    async addRate() {
      this.rateLoading = true;
      try {
        const id = this.guid();
        const optionId = this.guid();
        const rate = {
          id,
          serviceOptions: [{
            id: optionId,
            label: 'Standard Shipping',
            charge: '5',
          }],
          type: 'FLAT_RATE',
          from: '0',
          to: '1',
          priority: '1',
        };
        this.rates.splice(0, 0, _.cloneDeep(rate));
        this.$set(this.rateItems, id, {
          edit: true,
          delete: false,
          error: false,
        });
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        await this._reportError({
          error: e,
          fn: 'addRate store shipping',
        });
      } finally {
        this.rateLoading = false;
      }
    },
    removeServiceOption(rate, index) {
      rate.serviceOptions.splice(index, 1);
    },
    addServiceOption(rate) {
      rate.serviceOptions.push({
        id: this.guid(),
        label: 'Shipping Service Label',
        charge: '5',
      });
    },
    updateNumValue({ evt, key, rate }) {
      let value = evt.target.value || 0;
      if (value < 0) {
        value = 0;
      }
      this._debounce(() => {
        rate[key] = value;
      }, 500);
    },
    removeRate(index, id) {
      this.rates.splice(index, 1);
      if (this.rateItems[id]) {
        delete this.rateItems[id];
      }
    },
    async saveChanges() {
      this.loading = true;
      try {
        this.rateError = false;
        this.rates.forEach((rate) => {
          const from = this._getNumber(rate.from);
          const to = this._getNumber(rate.to);
          const priority = this._getNumber(rate.priority);
          const negativeCharges = (rate.serviceOptions
            .map(o => this._getNumber(o.charge))).filter(c => c < 0);
          if (rate.type !== 'FLAT_RATE' &&
            (to < from || negativeCharges.length > 0 || from < 0 || to < 0 || priority < 0)) {
            this.$set(this.rateItems[rate.id], 'error', true);
            this.rateError = true;
          } else {
            this.$set(this.rateItems[rate.id], 'error', false);
          }
        });
        if (this.rateError) {
          return;
        }
        _.forOwn(this.rateItems, (rate, id) => {
          this.rateItems[id].edit = false;
        });
        this.shippingCheck();
        const { products } = this.currentSite.dynamic;
        this.$store.dispatch('setGlobalValue', {
          item: products.shipping,
          key: 'to',
          value: _.cloneDeep(this.to),
        });
        this.$store.dispatch('setGlobalValue', {
          item: products.shipping,
          key: 'rates',
          value: _.cloneDeep(this.rates),
        });
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        if (!_.isEqual(this.to, this.initialEdits.to)) {
          // We only want to re-generate the site if the store's
          // shipping to countries change
          await this.callBuilder({
            publish: 'site',
            site: {
              _id: this.currentSite._id,
            },
          });
        }
        this.$set(this.initialEdits, 'to', _.cloneDeep(this.to));
        this.$set(this.initialEdits, 'rates', _.cloneDeep(this.rates));
        this.$set(this.initialEdits, 'rateItems', _.cloneDeep(this.rateItems));
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'success',
          time: 2000,
          message: 'Store changes saved!',
        });
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        await this._reportError({
          error: e,
          fn: 'saveChanges store shipping',
        });
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'error',
          message,
        });
      } finally {
        this.loading = false;
      }
    },
    checkToCountry(e, params) {
      const checked = e.target.checked;
      if (checked) {
        // if global, need to clear all else out
        if (params.value.iso === 'GLOBAL') {
          this.to = {};
        } else {
          this.$delete(this.to, 'AAA_GLOBAL');
        }
        this.$set(this.to, params.code, _.cloneDeep(params.value));
      } else if (this.to[params.code]) {
        this.$delete(this.to, params.code);
      }
      if (_.keys(this.to).length < 1) {
        this.$set(this.to, 'AAA_GLOBAL', _.cloneDeep(AAA_GLOBAL));
      }
    },
    cancelChanges() {
      this.$set(this, 'to', _.cloneDeep(this.initialEdits.to));
      const { shipping } = this.storeData;
      if (shipping.rates) {
        this.$set(this, 'rates', _.cloneDeep(this.initialEdits.rates));
        this.$set(this, 'rateItems', _.cloneDeep(this.initialEdits.rateItems));
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/store/shipping/index.js